import { Menu, Typography, useTheme } from '@mui/material';
import { Add, Check } from '@mui/icons-material';
import { styled } from '@mui/material';
import { useMemo, useState } from 'react';
import { Icon } from '@assets/Icon';
import { useDebounce } from '@hooks/useDebounce';
import { orderBy } from 'lodash';
interface AutoCompleteProps {
  options: { label: string; value: string }[];
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  actionButtonText?: string;
  actionButtonOnClick?: () => void;
  anchorEl: null | HTMLElement;
  handleClose: () => void;
}

export const AutoComplete = ({
  options,
  value,
  onChange,
  placeholder,
  actionButtonText,
  actionButtonOnClick,
  anchorEl,
  handleClose,
}: AutoCompleteProps) => {
  const theme = useTheme();

  const [searchValue, setSearchValue] = useState('');
  const open = Boolean(anchorEl);

  const debounceSearch = useDebounce(searchValue, 100);

  const filterOptions = useMemo(() => {
    return orderBy(options, 'label').filter((optionValue) =>
      optionValue.label
        ?.toLowerCase()
        .includes(debounceSearch.toLocaleLowerCase())
    );
  }, [options, debounceSearch]);

  const noResultsFound = filterOptions.length === 0 && searchValue.length !== 0;

  return (
    <Menu
      open={open}
      onClose={handleClose}
      anchorEl={anchorEl}
      sx={{
        '& .MuiPaper-root': {
          backgroundColor:
            theme.palette.mode === 'light' ? '#fcfcfc' : '#1d1d1d',

          borderRadius: '6px',
          border: `1px solid ${theme.palette.divider}`,
          minWidth: '286px',
          maxHeight: '500px',
          overflowY: 'auto',
        },
        '& .MuiList-root': {
          padding: 0,
        },
      }}
    >
      <SearchInputContainer>
        <Icon name="search" />
        <input
          type="text"
          placeholder={placeholder || 'Search'}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{
            all: 'unset',
            flex: 1,
            fontSize: '14px',
            color: theme.palette.text.primary,
            padding: 12,
          }}
        />
      </SearchInputContainer>

      <OptionsContainer>
        {noResultsFound && (
          <NotFoundContainer>
            <Typography variant="subtitle2" color="inherit">
              No results found
            </Typography>
          </NotFoundContainer>
        )}

        {(filterOptions || []).map((menuOption) => {
          const isOptionSelected = value === menuOption.value;

          return (
            <Option
              key={menuOption.value}
              onClick={() => {
                onChange(menuOption.value);
                handleClose();
              }}
              sx={{
                backgroundColor: isOptionSelected
                  ? theme.palette.action.hover
                  : 'transparent',
              }}
            >
              <Typography variant="subtitle2" color={'inherit'}>
                {menuOption.label}
              </Typography>
              {isOptionSelected && (
                <Check
                  sx={{
                    fontSize: 'inherit',
                    color: 'inherit',
                  }}
                />
              )}
            </Option>
          );
        })}
      </OptionsContainer>
      {searchValue.length === 0 && actionButtonText && actionButtonOnClick && (
        <ButtonContainer>
          <ActionButton
            onClick={() => {
              handleClose();
              actionButtonOnClick();
            }}
          >
            <Add
              sx={{
                fontSize: 'inherit',
                color: 'inherit',
              }}
            />
            <Typography variant="subtitle2" color="inherit">
              {actionButtonText}
            </Typography>
          </ActionButton>
        </ButtonContainer>
      )}
    </Menu>
  );
};

const NotFoundContainer = styled('div')(({ theme }) => ({
  color: theme.palette.mode === 'light' ? '#525252' : '#b4b4b4',
  display: 'flex',
  height: '64px',
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  fontWeight: 500,
}));

const ActionButton = styled('button')(({ theme }) => ({
  all: 'unset',
  display: 'flex',
  alignItems: 'center',
  padding: '6px 8px',
  gap: '8px',
  flex: 1,
  transitionProperty:
    'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter, -webkit-backdrop-filter',
  transitionTimingFunction: 'cubic-bezier(.4,0,.2,1)',
  transitionDuration: '.15s',
  '&:hover': {
    background: theme.palette.action.hover,
    cursor: 'pointer',
  },
}));

const ButtonContainer = styled('div')(({ theme }) => ({
  height: 36,
  padding: '4px',
  borderTop: `1px solid ${theme.palette.divider}`,
  color: theme.palette.mode === 'light' ? '#525252' : '#b4b4b4',
  display: 'flex',
}));

const Option = styled('li')(({ theme }) => ({
  height: '28px',
  padding: '6px 8px',
  borderRadius: '4px',
  alignItems: 'center',
  display: 'flex',
  justifyContent: 'space-between',
  color: theme.palette.mode === 'light' ? '#525252' : '#b4b4b4',
  transitionProperty:
    'color, background-color, border-color, text-decoration-color, fill, stroke, opacity, box-shadow, transform, filter, backdrop-filter, -webkit-backdrop-filter',
  transitionTimingFunction: 'cubic-bezier(.4,0,.2,1)',
  transitionDuration: '.15s',
  '&:hover': {
    background: theme.palette.action.hover,
    cursor: 'pointer',
  },
}));

const OptionsContainer = styled('ul')(() => ({
  padding: '4px',
}));

const SearchInputContainer = styled('div')(({ theme }) => ({
  padding: '0px 12px',
  display: 'flex',
  alignItems: 'center',
  height: '37px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));
